import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import moment from 'moment';

const savedFilters: any = {};

const belongsToDefaultFilters = {
  yardCompanyName: '',
  yardCompanyId: '',
  country: '',
  city: '',
  yardName: '',
  yardId: '',
  clientName: '',
  clientId: '',
};

const customDefaultFilters = (key: string) => {
  if (key === 'belongs-to') return getSavedSearchParams(belongsToDefaultFilters);

  return null;
};

const disabledFilters = (pathname: string, filterKey: string) => {
  const firstPath = pathname.split('/')[1];

  if (firstPath === 'marketplace' && filterKey === 'code') return true;
  return false;
};

export const useSaveFilters = (key = '', saveFilters: boolean, defaultFilters = {}) => {
  const { pathname } = useLocation();
  const [, setSearchParams] = useSearchParams();
  defaultFilters = customDefaultFilters(key) || defaultFilters;
  let oldFilters = savedFilters[pathname + key]?.filters || defaultFilters;

  if (pathname !== savedFilters[pathname + key]?.pathname) oldFilters = defaultFilters;

  const [filtersState, setFiltersState] = useState<any>(oldFilters);

  useEffect(() => {
    if (!savedFilters[pathname + key]) savedFilters[pathname + key] = {};

    if (!saveFilters) return;
    if (pathname === savedFilters[pathname + key]?.pathname) return;

    savedFilters[pathname + key].pathname = pathname;
    savedFilters[pathname + key].filters = defaultFilters;
  }, [pathname]);

  useEffect(() => {
    setFiltersState(oldFilters);
  }, [pathname, key, saveFilters]);

  useEffect(() => {
    if (saveFilters) saveSearchParams(true);
  }, []);

  const saveSearchParams = (withOutDelete = false) => {
    if (!saveFilters) return;

    const newSearchParams = new URLSearchParams(window.location.search);
    let isDeleted = false;

    Object.keys(savedFilters[pathname + key].filters).forEach((filterKey) => {
      if (disabledFilters(pathname, filterKey)) return;

      const value = savedFilters[pathname + key].filters[filterKey];
      if (value) {
        if (moment(value, moment.ISO_8601, true).isValid()) {
          newSearchParams.set(filterKey, moment(value).format('YYYY-MM-DD'));
        } else {
          newSearchParams.set(filterKey, value);
        }
      } else if (typeof value !== 'boolean' && typeof value !== 'number') {
        isDeleted = true;
        delete savedFilters[pathname + key].filters[filterKey];
        newSearchParams.delete(filterKey);
      }
    });

    if (!withOutDelete) {
      const keys = Object.values(savedFilters)
        .map((data: any) => Object.keys(data?.filters || {}))
        .flat();

      newSearchParams.forEach((_, key) => {
        if (keys.includes(key)) return;

        isDeleted = true;
        newSearchParams.delete(key);
      });
    }

    const newParams = Object.fromEntries(newSearchParams.entries());
    const oldParams = Object.fromEntries(new URLSearchParams(window.location.search).entries());
    const isEqual = Object.keys(newParams).every((key) => newParams[key] === oldParams[key]);
    if (isEqual && !isDeleted) return;

    setSearchParams(newSearchParams, { replace: true });
  };

  const handleChangeFilters = (data: any) => {
    if (!savedFilters[pathname + key]) {
      savedFilters[pathname + key] = {};
      savedFilters[pathname + key].pathname = pathname;
    }

    savedFilters[pathname + key].filters = data;
    setFiltersState(data);

    saveSearchParams();
  };

  return [saveFilters ? oldFilters : filtersState, handleChangeFilters];
};

export const useSaveFiltersControlEffect = () => {
  const { pathname } = useLocation();
  const secondPath = pathname.split('/')[2];

  useEffect(() => {
    Object.keys(savedFilters)
      .filter((key) => !key.includes(secondPath))
      .forEach((key) => delete savedFilters[key]);
  }, [pathname]);
};

export const getSavedFilters = (key = '') => {
  const pathname = window.location.pathname;
  const cached = savedFilters[pathname + key];

  if (cached?.filters && pathname === cached.pathname) {
    return cached.filters;
  }

  return customDefaultFilters(key) || {};
};

export const getSavedSearchParams = (defaultFilters: any) => {
  const urlSearch = window.location.search;
  const params = new URLSearchParams(urlSearch);

  return Object.keys(defaultFilters).reduce((acc: any, key) => {
    if (!params.has(key)) return acc;
    if (!params.get(key)) return acc;

    const isDate = moment(params.get(key), moment.ISO_8601, true).isValid();

    if (isDate) {
      acc[key] = moment(params.get(key));
      return acc;
    }

    if (key === 'number' && (params.get(key)?.split(',').length ?? 0) > 1) {
      acc[key] = params.get(key)?.split(',');
      return acc;
    }

    acc[key] = params.get(key);
    return acc;
  }, {} as any);
};

export const clearSavedFilters = () => {
  Object.keys(savedFilters).forEach((key) => delete savedFilters[key]);
};
